import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import QuickActions from "@/components/QuickActions";
import { useBranding, useHeroFallbackMode } from "@/hooks/useCms";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
    <motion.span
      custom={i}
      variants={line}
      initial="hidden"
      animate="show"
      className={`block leading-[1.02] ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVideo = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const { heroVideo, heroPoster, heroTitle, heroSubtitle, heroDescription, heroMobileFallbackEnabled, heroVideoStartDelay } = useBranding();
  // Once the video has actually rendered its first frame we can hide the still image.
  const [videoReady, setVideoReady] = useState(false);
  // Vídeo bufferizado o suficiente para reproduzir sem travar.
  const [videoCanPlay, setVideoCanPlay] = useState(false);
  // Tempo mínimo de imagem de arranque já decorrido.
  const [delayElapsed, setDelayElapsed] = useState(false);
  const videoRef = useRef(null);
  // Mobile / slow-connection detection — null enquanto se avalia; true = servir só imagem.
  const fallbackMode = useHeroFallbackMode();
  const useVideo = heroMobileFallbackEnabled === false || fallbackMode === false;

  // Arranca o contador do delay (imagem de arranque mostrada durante N segundos).
  useEffect(() => {
    if (!useVideo) return undefined;
    const ms = Math.max(0, (Number(heroVideoStartDelay) || 0) * 1000);
    const timer = setTimeout(() => setDelayElapsed(true), ms);
    return () => clearTimeout(timer);
  }, [useVideo, heroVideoStartDelay]);

  // Só reproduz o vídeo quando (a) o delay passou E (b) já está pronto para tocar.
  useEffect(() => {
    if (useVideo && delayElapsed && videoCanPlay && videoRef.current) {
      const v = videoRef.current;
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      setVideoReady(true);
    }
  }, [useVideo, delayElapsed, videoCanPlay]);

  // Divide subtítulo em linhas — respeita quebras \n colocadas no backoffice.
  const subtitleLines = (heroSubtitle || "").split(/\r?\n/).filter((s) => s.length > 0);

  return (
    <section
      id="top"
      ref={ref}
      className="relative lg:min-h-[100svh] pt-[72px] pb-14 lg:pb-16 flex flex-col overflow-hidden bg-[#050A14]"
      data-testid="hero-section"
      data-hero-mode={useVideo ? "video" : "image"}
    >
      {/* Video / Image background */}
      <motion.div
        style={{ y: yVideo, scale: 1.08 }}
        className="absolute top-[72px] left-0 right-0 bottom-0 z-0 pointer-events-none overflow-hidden"
      >
        {/* Poster image (frame 0 do vídeo) — sempre presente. Em mobile/slow-connection é a ÚNICA
            imagem visível (vídeo não é carregado). Em desktop cobre o vídeo até o browser
            ter renderizado o primeiro frame, eliminando qualquer flash de transição. */}
        <img
          src={heroPoster}
          alt=""
          aria-hidden="true"
          draggable="false"
          data-testid="hero-poster-img"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${
            useVideo && videoReady ? "opacity-0" : "opacity-100"
          }`}
        />
        {useVideo && (
          <video
            key={heroVideo}
            ref={videoRef}
            muted
            loop
            playsInline
            preload="auto"
            poster={heroPoster}
            onCanPlay={() => setVideoCanPlay(true)}
            onCanPlayThrough={() => setVideoCanPlay(true)}
            data-testid="hero-video"
            className="relative w-full h-full object-cover"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
        )}
      </motion.div>

      {/* Overlay */}
      <div className="absolute top-[72px] left-0 right-0 bottom-0 z-[1] hero-overlay pointer-events-none" />

      {/* Conteúdo */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full flex-1 flex flex-col justify-center gap-8 lg:gap-10 py-6 lg:py-8">
        <div className="max-w-3xl">
          <h1
            className="hero-title font-heading tracking-tight text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.5)]"
            data-testid="hero-title"
          >
            <MaskedLine i={0}>{heroTitle}</MaskedLine>
            {subtitleLines.length > 0 ? (
              subtitleLines.map((ln, idx) => (
                <MaskedLine key={idx} i={idx + 1} className="text-[#4FC6D8]">
                  {ln}
                </MaskedLine>
              ))
            ) : (
              <MaskedLine i={1} className="text-[#4FC6D8]">
                {heroSubtitle}
              </MaskedLine>
            )}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className="hero-sub mt-5 lg:mt-6 text-white/85 max-w-2xl whitespace-pre-line"
          >
            {heroDescription}
          </motion.p>
        </div>
        <QuickActions />
      </div>
    </section>
  );
}
